import type { BasisPoints } from "./permissions.js";
import type { CurrencyCode } from "./money.js";
import type { TimeZone } from "./time.js";
import { DEFAULT_BUSINESS_SETTINGS } from "./defaults.js";

/** OTP delivery channels, tried in the business's configured order (SPEC §3). */
export const OTP_CHANNELS = ["whatsapp", "sms", "email", "front_desk_code"] as const;
export type OtpChannel = (typeof OTP_CHANNELS)[number];

export interface TaxSettings {
  /** Tax system label shown on invoices, e.g. "GST", "VAT". Logic must not branch on it being "GST". */
  system: string;
  /** Default rate for new items, in basis points (1800 = 18%). Items may override. */
  defaultRateBp: BasisPoints;
  /** Whether new item prices are entered tax-inclusive. */
  pricesIncludeTax: boolean;
}

/** Business-wide settings. Every field has a default in `defaults.ts`; stored overrides win. */
export interface BusinessSettings {
  currency: CurrencyCode;
  timezone: TimeZone;
  /** ISO 3166-1 alpha-2. */
  country: string;
  /** BCP 47, used for number/date formatting. */
  locale: string;
  /** Month the financial year starts, 1–12 (India: 4 = April). */
  fyStartMonth: number;
  tax: TaxSettings;
  /** OTP fallback order. */
  otpChannels: OtpChannel[];
  /** Sliding session length on a trusted device, in days (30–90, SPEC §3). */
  sessionTtlDays: number;
  /** Business-level tax id (GSTIN in India). Branches inherit it unless they set their own. null = none. */
  taxId: string | null;
  /** Business-level payment account reference. Branches inherit it unless they set their own. null = none. */
  paymentAccountRef: string | null;
  // --- billing: section reserved by the billing rails commit; billing owns it ---
  billing?: BillingSettings;
  // --- invoicing: section reserved for the invoices slice ---
  invoicing?: InvoicingSettings;
  // --- payments: section reserved for the payments slice ---
  payments?: PaymentsSettings;
  // --- memberships: section reserved for the membership-actions slice ---
  memberships?: MembershipsSettings;
  // --- pt: section reserved for the pt-classes slice ---
  pt?: PtSettings;
  // --- ptSchedule: fixed-slot PT booking and auto-marking (pt-schedules slice) ---
  ptSchedule?: PtScheduleSettings;
  // --- classes: section reserved for the pt-classes slice ---
  classes?: ClassesSettings;
  // --- attendance: section reserved for the staff-attendance slice ---
  attendance?: AttendanceSettings;
  // --- leads: section reserved for the lead-intake slice ---
  leads?: LeadsSettings;
  // --- checkin: section reserved for the kiosk slice ---
  checkin?: CheckinSettings;
  // --- commission: section reserved for the payroll slice ---
  commission?: CommissionSettings;
  // --- payroll: section reserved for the payroll slice ---
  payroll?: PayrollSettings;
  // --- messaging: section reserved for the messaging slice ---
  messaging?: MessagingSettings;
  // --- biometric: section reserved for the biometric-bridge slice ---
  biometric?: BiometricSettings;
  // --- expenses: section reserved for the expenses-reports slice ---
  expenses?: ExpensesSettings;
  // --- followups: section reserved for the followups-tasks-api slice ---
  followups?: FollowupsSettings;
}

// --- billing settings (billing slice) ---
export interface BillingSettings {
  /** Round a bill's total to the nearest multiple of this many minor units (100 = whole rupee). Absent = no round-off. */
  roundOffUnit?: number;
  /** When true, every plan kind queues behind every other kind instead of only its own. Default false. */
  staggerAcrossKinds?: boolean;
}
// --- invoicing settings (invoices slice) ---
export interface InvoicingSettings {
  /** Seller's legal name on tax documents. Required before a registered seller can issue. null = not set. */
  legalName: string | null;
  invoicePrefix: string;
  billOfSupplyPrefix: string;
  creditNotePrefix: string;
  /** Minimum digits in the sequence part of a number. */
  seqWidth: number;
  /** Longest allowed document number. To verify with a CA: the default follows Rule 46 as recalled. */
  maxNumberLength: number;
  /** One regular-expression character class, `^[...]+$`, every number must match. To verify with a CA. */
  numberPattern: string;
}
// --- payments settings (payments slice) ---
export interface PaymentsSettings {}
// --- memberships settings (membership-actions slice) ---
export interface MembershipsSettings {
  /** Most frozen days across one plan's life. */
  maxFreezeDaysPerPlan: number;
  /** Most freezes on one plan. */
  maxFreezesPerPlan: number;
  /** A freeze must start at least this many days after the day it is asked for. 0 = may start today. */
  minNoticeDays: number;
  /** Catalogue item billed for each freeze (a fee or service item). null = freezes are free. */
  feeItemId: string | null;
}

// --- pt settings (pt-classes slice) ---
/** Same shape as `PtPolicy` in @helmr/scheduling; stored apart from BusinessSettings (settings key `pt`). */
export interface PtSettings {
  /** Cancelling with at least this many hours' notice is early (boundary = early). */
  cancelWindowHours: number;
  /** A reschedule needs this many hours' notice before the original start. */
  rescheduleMinNoticeHours: number;
  /** Most reschedules along one session chain. */
  maxReschedules: number;
  /** Which outcomes use up a pack session. An early cancel never does. */
  consume: { completed: boolean; no_show: boolean; late_cancel: boolean };
}
// --- PT schedule settings (pt-schedules slice) ---
/** How a PT pack is booked. `daily` is `fixed_slot` preset to Monday–Saturday. */
export type PtBookingStyle = "fixed_slot" | "daily" | "flexible";
export const PT_BOOKING_STYLES: readonly PtBookingStyle[] = ["fixed_slot", "daily", "flexible"];
/** Stored apart from BusinessSettings (settings key `ptSchedule`). */
export interface PtScheduleSettings {
  /** Style for a PT plan that does not set its own. */
  defaultBookingStyle: PtBookingStyle;
  /** fixed_slot / daily packs only: a check-in completes the day's session; at day end an unmarked one becomes a no-show. */
  autoMark: boolean;
}
// --- classes settings (pt-classes slice) ---
/** Same shape as `ClassPolicy` in @helmr/scheduling; stored apart from BusinessSettings (settings key `classes`). */
export interface ClassesSettings {
  cancelWindowHours: number;
  lateCancelConsumes: boolean;
  noShowConsumes: boolean;
  /** null = unlimited. */
  waitlistLimit: number | null;
  autoPromote: boolean;
}
// --- attendance settings (staff-attendance slice) ---
export interface AttendanceSettings {
  /** An `out` within this many hours of an open `in` closes that shift, even past midnight. */
  maxShiftHours: number;
  /** Two app/kiosk punches closer than this many seconds are one punch. */
  debounceSeconds: number;
  /** Worked minutes for a full day (`present`). */
  fullDayMinutes: number;
  /** Worked minutes for `half_day`; below this the day is `absent`. */
  halfDayMinutes: number;
}
// --- leads settings (lead-intake slice) ---
export interface LeadsSettings {
  /** Same phone within this many days of the lead's last activity merges into it. */
  dedupeWindowDays: number;
  /** First call due this many minutes after receipt (counted from opening if received after hours). */
  firstResponseMinutes: number;
  /** Ordered; first match wins. Source match is case-insensitive; both conditions must match when both are given. */
  assignmentRules: { id: string; match: { sources?: string[]; branchIds?: string[] }; owners: string[] }[];
  /** Fallback round-robin pool (staff ids) when no rule matches. */
  defaultOwners: string[];
  /** Index = day of week, 0 = Sunday; null = closed. Absent = the engagement package's default hours. */
  businessHours?: ({ open: string; close: string } | null)[];
  /** Country calling code a number without one is read in. */
  defaultCountryCode: string;
  /** Digits in a national number without trunk prefix. */
  nationalNumberLength: number;
}
// --- checkin settings (kiosk slice) ---
export interface CheckinSettings {
  /** How long one signing key lives, in days. The longest a stolen kiosk's key set stays useful. */
  keyValidityDays: number;
  /** The next key is published this many days before the current one ends. */
  keyPublishLeadDays: number;
  /** Offline events older than this many hours are rejected, not just flagged. Must be above 0: there is no "unlimited". */
  maxOfflineAcceptHours: number;
  /** Offline events older than this many hours are accepted but flagged `late`. */
  maxLateHours: number;
  /** Length of one member-code window, in seconds. */
  windowSeconds: number;
  /** Member codes from this many windows either side are accepted (clock skew). */
  skewWindows: number;
  /** Wall codes this many windows old are still fresh. */
  wallMaxAgeWindows: number;
  /** Deny entries outside the branch's opening hours. */
  enforceBranchHours: boolean;
  /** Amber when a plan ends within this many days. */
  expiryWarnDays: number;
  /** Amber when the member has an unpaid balance. */
  warnOnBalanceDue: boolean;
}
// --- commission settings (payroll slice) ---
/** The four gym decisions the commission engine reads (SPEC §11). Same names and values as `@helmr/commission`. */
export interface CommissionSettings {
  unusedAtExpiry: "no_earning" | "pay_remaining_to_assigned_trainer";
  noShowLateCancel: "counts" | "does_not_count";
  deliveredButUnpaid: "pay_anyway" | "hold_until_paid" | "pro_rata_paid";
  heldAfterPartRefund: "recompute_on_effective_price" | "owner_releases_manually";
}
// --- payroll settings (payroll slice) ---
export interface PayrollSettings {
  /** TDS rate, in basis points, for a contractor whose profile sets none. Null = not set: drafting a contractor is refused until it is. To verify with a CA (SPEC §11). */
  contractorTdsBp: number | null;
}

// --- messaging settings (messaging slice) ---
export interface MessagingSettings {
  /** Silent until someone turns it on: at cutover hundreds of imported plans and dues arrive at once. Defaults to true. */
  paused: boolean;
  /** Language of the gym's approved templates (D30), a provider language code such as `en` or `ta`. */
  templateLanguage: string;
  /** Tried when no approved template exists in `templateLanguage`. null = send nothing instead. */
  fallbackLanguage: string | null;
  /** Inbound text equal to one of these (trimmed, case-folded) stops reminders and marketing. */
  optOutKeywords: string[];
  /** Inbound text equal to one of these clears an opt-out. */
  optInKeywords: string[];
  /** Log rows older than this are deleted; params are nulled at 30 days. */
  logRetentionDays: number;
  /** A message more than this many hours past its send time is never sent. */
  maxLateHours: number;
  /** Most reminder-class messages one person gets per local day. */
  maxPerRecipientPerDay: number;
  /** Per-type timing overrides, passed to the engagement scheduler (enabled, offsets, sendTime, quietHours, channels). */
  types: Record<string, { enabled?: boolean; offsets?: number[]; sendTime?: string; quietHours?: { start: string; end: string } | null; channels?: ("whatsapp" | "email")[] }>;
}
// --- biometric settings (biometric-bridge slice) ---
// Stored apart from BusinessSettings (like `attendance`). Biometrics are off until a business opts in (D49).
export interface BiometricSettings {
  /** Master switch. Off until the business opts in; `POST /bridges` is refused while it is false. */
  enabled: boolean;
  /** Cross-branch template copy (D49). Off by default, and also behind the BIOMETRIC_COPY_ENABLED Worker variable. */
  crossBranchCopy: boolean;
  /** ISO country codes where biometrics are refused. US launch is QR only (BIPA, D49). Data, not a code branch. */
  blockedCountries: string[];
  /** Device uids no one may be mapped to, enrolled as, written or deleted. 999 is the ESSL master user. */
  reservedUids: number[];
  /** Days after a plan ends before a terminal disables the person. */
  accessGraceDays: number;
}
// --- expenses settings (expenses-reports slice) ---
export interface ExpensesSettings {
  /** Payment modes an expense may name. Removing one never invalidates old rows. Default: cash, upi, bank_transfer, card, cheque. */
  paymentModes: string[];
}
// --- followups settings (followups-tasks-api slice) ---
export interface FollowupsSettings {
  /** A membership ending within this many days shows as a renewal follow-up. */
  renewalLeadDays: number;
  /** A bill instalment due within this many days (or past due) shows as a payment follow-up. */
  dueLeadDays: number;
}

/** Stored overrides: any subset; `tax` may itself be partial. */
export type BusinessSettingsOverrides = Partial<Omit<BusinessSettings, "tax">> & { tax?: Partial<TaxSettings> };

/** Merge stored overrides onto the defaults. Arrays replace, they do not merge. Throws on invalid values. */
export function resolveSettings(
  overrides: BusinessSettingsOverrides = {},
  defaults: BusinessSettings = DEFAULT_BUSINESS_SETTINGS,
): BusinessSettings {
  // Checked here because spreading a stored string would turn it into an array of letters.
  if (overrides.otpChannels != null && !Array.isArray(overrides.otpChannels)) {
    throw new RangeError(`settings.otpChannels must be an array, got ${String(overrides.otpChannels)}`);
  }
  const merged: BusinessSettings = {
    ...defaults,
    ...stripUndefined(overrides),
    tax: { ...defaults.tax, ...stripUndefined(overrides.tax ?? {}) },
    otpChannels: [...(overrides.otpChannels ?? defaults.otpChannels)],
  };
  validateSettings(merged);
  return merged;
}

function stripUndefined<T extends object>(o: T): Partial<T> {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as Partial<T>;
}

function validateSettings(s: BusinessSettings): void {
  if (!/^[A-Z]{3}$/.test(s.currency)) throw new RangeError(`settings.currency must be ISO 4217, got ${s.currency}`);
  if (!/^[A-Z]{2}$/.test(s.country)) throw new RangeError(`settings.country must be ISO 3166-1 alpha-2, got ${s.country}`);
  if (!Number.isInteger(s.fyStartMonth) || s.fyStartMonth < 1 || s.fyStartMonth > 12) {
    throw new RangeError(`settings.fyStartMonth must be 1–12, got ${s.fyStartMonth}`);
  }
  const bp = s.tax.defaultRateBp;
  if (!Number.isInteger(bp) || bp < 0 || bp > 10000) {
    throw new RangeError(`settings.tax.defaultRateBp must be an integer 0–10000, got ${bp}`);
  }
  if (typeof s.tax.system !== "string" || s.tax.system === "") {
    throw new RangeError(`settings.tax.system must be a non-empty string, got ${String(s.tax.system)}`);
  }
  if (typeof s.tax.pricesIncludeTax !== "boolean") {
    throw new RangeError(`settings.tax.pricesIncludeTax must be a boolean, got ${String(s.tax.pricesIncludeTax)}`);
  }
  if (!Number.isInteger(s.sessionTtlDays) || s.sessionTtlDays < 30 || s.sessionTtlDays > 90) {
    throw new RangeError(`settings.sessionTtlDays must be an integer 30–90, got ${s.sessionTtlDays}`);
  }
  for (const key of ["taxId", "paymentAccountRef"] as const) {
    const v = s[key];
    if (v !== null && (typeof v !== "string" || v.trim() === "" || v.length > 128)) {
      throw new RangeError(`settings.${key} must be null or a non-empty string up to 128 characters`);
    }
  }
  if (s.otpChannels.length === 0) throw new RangeError("settings.otpChannels must not be empty");
  for (const channel of s.otpChannels) {
    if (!OTP_CHANNELS.includes(channel)) throw new RangeError(`settings.otpChannels has an unknown channel: ${String(channel)}`);
  }
  try {
    if (typeof s.locale !== "string") throw new TypeError();
    Intl.getCanonicalLocales(s.locale);
  } catch {
    throw new RangeError(`settings.locale is not a valid BCP 47 tag: ${String(s.locale)}`);
  }
  try {
    new Intl.DateTimeFormat("en", { timeZone: s.timezone });
  } catch {
    throw new RangeError(`settings.timezone is not a valid IANA zone: ${s.timezone}`);
  }
}
